import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store';
import { adminApi } from '@/services/api';
import Button from '@/components/ui/Button';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import {
  ChartBarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  TagIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  original_price: number;
  product_type: 'brand_new' | 'recycled';
  category_id: number;
  category?: {
    id: number;
    name: string;
  };
  shop_id: number;
  shop?: {
    id: number;
    name: string;
    owner?: {
      first_name: string;
      last_name: string;
    };
  };
  brand?: string;
  model?: string;
  condition?: string;
  warranty?: string;
  is_public: boolean;
  is_featured: boolean;
  tags: string[];
  images: Array<{
    id: number;
    image_url: string;
  }>;
  created_at: string;
  updated_at: string;
}

interface ProductFilters {
  is_public?: boolean;
  product_type?: 'brand_new' | 'recycled';
  category_id?: number;
  shop_id?: number;
  is_featured?: boolean;
  search?: string;
}

const ProductModerationPage: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [shops, setShops] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ProductFilters>({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchShops();
  }, [filters]);

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await adminApi.getAllProducts();
      let filteredProducts = response.data;

      // Apply filters
      if (filters.is_public !== undefined) {
        filteredProducts = filteredProducts.filter((product: Product) => product.is_public === filters.is_public);
      }
      if (filters.product_type) {
        filteredProducts = filteredProducts.filter((product: Product) => product.product_type === filters.product_type);
      }
      if (filters.category_id) {
        filteredProducts = filteredProducts.filter((product: Product) => product.category_id === filters.category_id);
      }
      if (filters.shop_id) {
        filteredProducts = filteredProducts.filter((product: Product) => product.shop_id === filters.shop_id);
      }
      if (filters.is_featured !== undefined) {
        filteredProducts = filteredProducts.filter((product: Product) => product.is_featured === filters.is_featured);
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredProducts = filteredProducts.filter((product: Product) =>
          product.name.toLowerCase().includes(searchLower) ||
          product.description.toLowerCase().includes(searchLower) ||
          product.brand?.toLowerCase().includes(searchLower) ||
          product.model?.toLowerCase().includes(searchLower) ||
          product.shop?.name.toLowerCase().includes(searchLower)
        );
      }

      setProducts(filteredProducts);
    } catch (error: any) {
      setError(error.detail || 'Failed to fetch products');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminApi.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await adminApi.getAllShops();
      setShops(response.data);
    } catch (error) {
      console.error('Failed to fetch shops:', error);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchQuery }));
  };

  const handleFilterChange = (key: keyof ProductFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchQuery('');
  };

  const handleApproveProduct = async (productId: number) => {
    try {
      await adminApi.approveProduct(productId);
      await fetchProducts();
      setSelectedProduct(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to approve product');
    }
  };

  const handleRejectProduct = async (productId: number, reason: string) => {
    try {
      await adminApi.rejectProduct(productId, reason);
      await fetchProducts();
      setSelectedProduct(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to reject product');
    }
  };

  const handleToggleFeatured = async (productId: number, isFeatured: boolean) => {
    try {
      await adminApi.toggleProductFeatured(productId, isFeatured);
      await fetchProducts();
      setSelectedProduct(null);
    } catch (error: any) {
      setError(error.detail || 'Failed to update product feature status');
    }
  };

  const getStatusColor = (isPublic: boolean) => {
    return isPublic ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusLabel = (isPublic: boolean) => {
    return isPublic ? 'Approved' : 'Pending';
  };

  const getProductTypeColor = (type: string) => {
    return type === 'brand_new' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Product Moderation</h1>
              <p className="mt-2 text-gray-600">
                Review, approve, and manage products on your platform
              </p>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <FunnelIcon className="h-5 w-5 mr-2" />
                Filters
              </Button>
              <Button onClick={fetchProducts}>
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md mb-6">
            {error}
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products by name, description, brand, or shop..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <Button onClick={handleSearch} className="px-6">
              Search
            </Button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filters.is_public === undefined ? '' : filters.is_public.toString()}
                    onChange={(e) => handleFilterChange('is_public', e.target.value === '' ? undefined : e.target.value === 'true')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="false">Pending</option>
                    <option value="true">Approved</option>
                  </select>
                </div>

                {/* Product Type Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type
                  </label>
                  <select
                    value={filters.product_type || ''}
                    onChange={(e) => handleFilterChange('product_type', e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Types</option>
                    <option value="brand_new">Brand New</option>
                    <option value="recycled">Recycled</option>
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    value={filters.category_id || ''}
                    onChange={(e) => handleFilterChange('category_id', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Shop Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Shop
                  </label>
                  <select
                    value={filters.shop_id || ''}
                    onChange={(e) => handleFilterChange('shop_id', e.target.value ? Number(e.target.value) : undefined)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">All Shops</option>
                    {shops.map((shop) => (
                      <option key={shop.id} value={shop.id}>
                        {shop.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
              {/* Product Image */}
              <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                <img
                  src={getPrimaryImage(product.images)}
                  alt={product.name}
                  className="w-full h-48 object-cover"
                />
              </div>

              {/* Product Info */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{product.description}</p>
                  </div>
                  <div className="flex flex-col space-y-1 ml-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(product.is_public)}`}>
                      {getStatusLabel(product.is_public)}
                    </span>
                    {product.is_featured && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        Featured
                      </span>
                    )}
                  </div>
                </div>

                {/* Product Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Price:</span>
                    <span className="text-sm font-medium text-gray-900">{formatCurrency(product.price)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Type:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProductTypeColor(product.product_type)}`}>
                      {product.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                    </span>
                  </div>

                  {product.category && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Category:</span>
                      <span className="text-sm text-gray-900">{product.category.name}</span>
                    </div>
                  )}

                  {product.shop && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Shop:</span>
                      <span className="text-sm text-gray-900">{product.shop.name}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProduct(product)}
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    Review
                  </Button>
                  
                  {!product.is_public && (
                    <Button
                      size="sm"
                      onClick={() => handleApproveProduct(product.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        )}

        {/* Product Detail Modal */}
        {selectedProduct && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Product Review</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedProduct(null)}
                  >
                    ×
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Product Images */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Product Images</h4>
                    {selectedProduct.images && selectedProduct.images.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {selectedProduct.images.map((image) => (
                          <img
                            key={image.id}
                            src={image.image_url}
                            alt={selectedProduct.name}
                            className="w-full h-32 object-cover rounded-md"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="w-full h-32 bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-gray-400">No images available</span>
                      </div>
                    )}
                  </div>

                  {/* Product Details */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">{selectedProduct.name}</h4>
                      <p className="text-gray-600 mt-1">{selectedProduct.description}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Price</label>
                        <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedProduct.price)}</p>
                        {selectedProduct.original_price > selectedProduct.price && (
                          <p className="text-sm text-gray-500 line-through">
                            {formatCurrency(selectedProduct.original_price)}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Type</label>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getProductTypeColor(selectedProduct.product_type)}`}>
                          {selectedProduct.product_type === 'brand_new' ? 'Brand New' : 'Recycled'}
                        </span>
                      </div>
                    </div>

                    {selectedProduct.brand && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Brand</label>
                        <p className="text-gray-900">{selectedProduct.brand}</p>
                      </div>
                    )}

                    {selectedProduct.model && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Model</label>
                        <p className="text-gray-900">{selectedProduct.model}</p>
                      </div>
                    )}

                    {selectedProduct.condition && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Condition</label>
                        <p className="text-gray-900">{selectedProduct.condition}</p>
                      </div>
                    )}

                    {selectedProduct.warranty && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Warranty</label>
                        <p className="text-gray-900">{selectedProduct.warranty}</p>
                      </div>
                    )}

                    {selectedProduct.category && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <p className="text-gray-900">{selectedProduct.category.name}</p>
                      </div>
                    )}

                    {selectedProduct.shop && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Shop</label>
                        <p className="text-gray-900">{selectedProduct.shop.name}</p>
                        {selectedProduct.shop.owner && (
                          <p className="text-sm text-gray-600">
                            Owner: {selectedProduct.shop.owner.first_name} {selectedProduct.shop.owner.last_name}
                          </p>
                        )}
                      </div>
                    )}

                    {selectedProduct.tags && selectedProduct.tags.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Tags</label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedProduct.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Status</label>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedProduct.is_public)}`}>
                          {getStatusLabel(selectedProduct.is_public)}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Created</label>
                        <p className="text-gray-900">{new Date(selectedProduct.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button variant="outline" onClick={() => setSelectedProduct(null)}>
                    Close
                  </Button>
                  
                  <Button
                    onClick={() => handleToggleFeatured(selectedProduct.id, !selectedProduct.is_featured)}
                    className={selectedProduct.is_featured ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-blue-600 hover:bg-blue-700'}
                  >
                    {selectedProduct.is_featured ? 'Remove from Featured' : 'Add to Featured'}
                  </Button>
                  
                  {!selectedProduct.is_public && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleRejectProduct(selectedProduct.id, 'Inappropriate content')}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        <XCircleIcon className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => handleApproveProduct(selectedProduct.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductModerationPage;